import React, { useCallback, useEffect, useState, useRef, useMemo } from "react";
import NextWeekIcon from "@mui/icons-material/NextWeek";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import Layout4 from "../../DataLayouts/Layout4";
import { useNavigate, useSearchParams } from "react-router-dom";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import AlarmAddIcon from "@mui/icons-material/AlarmAdd";
import LogoutIcon from "@mui/icons-material/Logout";
import { operatorRequiresValue } from "../../../Components/filters/operators";
import { getCoreScopeKey } from "../../../Configurations/coreSession";
import ListSummaryCards, {
  distinctCount,
  countWhere,
} from "../../../Components/List/ListSummaryCards";

export const DEFAULT_COLUMNS = [
  {
    field: "full_name",
    label: "Full Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },

  {
    field: "email",
    label: "Email",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "phone",
    label: "Phone",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "application_user_id",
  sortOrder: "desc",
};

// Helper functions
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  return filters;
};

function ApplicationUserList() {
  const [applicationUsers, setApplicationUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);

  const { userData } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);
  const gridScopeKey = getCoreScopeKey(userData);


 function formatDate(date) {
  if (!date || date === "-") return null;

  const d = new Date(date);
  // Check if date is valid
  if (isNaN(d.getTime())) return null;

  const day = String(d.getDate()).padStart(2, '0');      // 1 → "01"
  const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const year = d.getFullYear();

  return `${day}-${month}-${year}`;
}


  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const loadTableConfiguration = async () => {
      try {
        const configRes = await fetch(
          `${MAIN_URL}/api/general-datagrids`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `application_users_grid_${gridScopeKey}`;
          const savedConfig = datagrids.find(
            (dg) => dg.datagrid_key === orgKey
          );

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [gridScopeKey]);

  // load data
  useEffect(() => {
    if (!hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");
        const page = Number.parseInt(searchParams.get("page"), 10) || 1;
        const pageSize = Number.parseInt(searchParams.get("pageSize"), 10) || 10;
        const requestBody = {
          filters,
          page,
          pageSize,
          sort: {
            field: searchParams.get("sortBy") || "application_user_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        const fetchClientsFallback = async () => {
          const qs = new URLSearchParams();
          qs.set("per_page", String(pageSize));
          qs.set("page", String(page));
          if (search && search.trim() !== "") qs.set("search", search.trim());

          const res = await fetch(
            `${MAIN_URL}/api/client-management/application-users?${qs.toString()}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
              },
              signal: fetchControllerRef.current.signal,
            }
          );

          if (res.status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
            return { data: [], pagination: DEFAULT_PAGINATION };
          }

          if (!res.ok) {
            throw new Error(`HTTP error! status: ${res.status}`);
          }

          const json = await res.json();
          const payload = json?.data;
          const list = Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];

          // Laravel paginator shape (if present): { current_page, per_page, total, last_page, ... }
          const total =
            payload?.total ?? (Array.isArray(list) ? list.length : 0);
          const totalPages = payload?.last_page ?? 1;
          const currentPage = payload?.current_page ?? page;
          const perPage = payload?.per_page ?? pageSize;

          return {
            data: list,
            pagination: {
              page: currentPage,
              pageSize: perPage,
              total,
              totalPages,
              hasPrev: currentPage > 1,
              hasNext: currentPage < totalPages,
            },
          };
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/client-management/application-users/filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        if (response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
          return;
        }

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            } else {
              setPaginationData(DEFAULT_PAGINATION);
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const fallback = await fetchClientsFallback();
          data = fallback.data || [];
          setPaginationData(fallback.pagination || DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Filter out items that are actually unassigned (unassigned_on is not "-", null, undefined, or empty)
        let filteredApplicationUsers = data.filter((item) => {
          const unassignedOn = item.unassigned_on;
          // Keep items where unassigned_on is "-", null, undefined, or empty string (meaning still assigned)
          return !unassignedOn || unassignedOn === "-" || unassignedOn === "";
        });
        
        let applicationUsers = filteredApplicationUsers.map((item) => ({
          ...item,
          id: item.application_user_id ?? item.id,
          full_name: item.full_name ?? "-",
          email: item.email ?? "-",
          phone: item.phone ?? "-",
        }));
        setApplicationUsers(applicationUsers);
      } catch (err) {
        if (err.name === "AbortError") return;
        
                console.error("Failed to fetch application users:", err);
        toast.error("Failed to fetch application users");
        setApplicationUsers([]);
        setPaginationData(DEFAULT_PAGINATION);
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [searchParams]);

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const deleteRole = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/client-management/application-users/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        if (response.ok) {
          toast.success(data.message || "Application user deleted successfully");
          // Refresh data after deletion by triggering useEffect
          setSearchParams((prev) => new URLSearchParams(prev));
        } else {
          const errorMessage =
            data.message || 
            data.error || 
            data.errors?.[Object.keys(data.errors || {})[0]]?.[0] ||
            "Failed to delete application user";
          toast.error(errorMessage);
        }
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          toast.error(
            error.response?.data?.error || "Failed to delete application user"
          );
        }
        console.error("Delete failed:", error);
      }
    },
    [setSearchParams]
  );


  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/organization/application-user/edit/${item.id || item.application_user_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/organization/application-user/view/${item.id || item.application_user_id}`
      );
    },
    [navigate]
  );


  const summaryCards = useMemo(
    () => [
      {
        key: "total",
        label: "Total users",
        value: paginationData?.total ?? applicationUsers.length,
        iconKey: "users",
      },
      {
        key: "emails",
        label: "With email",
        value: countWhere(
          applicationUsers,
          (row) => row?.email && String(row.email).trim() !== ""
        ),
        iconKey: "userCheck",
        tone: "ok",
      },
      {
        key: "phones",
        label: "With phone",
        value: distinctCount(applicationUsers, "phone"),
        iconKey: "hash",
      },
    ],
    [applicationUsers, paginationData]
  );

  return (
    <>
      <Layout4
        loading={loading}
        delete_action={"CLIENT_DELETE"}
      heading={"Application Users"}
        add_action={"APPLICATION_USER_ADD"}
        btnName={""}
        Data={applicationUsers}
        Icons={[
          <AutorenewIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <NextWeekIcon color="primary" />,
          <AlarmAddIcon sx={{ color: "text.secondary" }} />,
          <LogoutIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "Application Users",
          "Application Users",
          "Add Application Users",
          "Application Users",
        ]}
        Route={"/organization/application-user"}
        setData={setApplicationUsers}
        DeleteFunc={deleteRole}
      />

      <ListSummaryCards cards={summaryCards} />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="application_users"
          primaryKey="application_user_id"
          heading="Application Users"
          data={applicationUsers}
          sortname="application_user_id"
          showActions={false}
          Route="/organization/application-user"
          DeleteFunc={deleteRole}
          EditFunc={handleEdit}
          edit_delete_action={["APPLICATION_USER_EDIT", "APPLICATION_USER_DELETE"]}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="application_users"
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default ApplicationUserList;
